import { Share2, Crown, Download } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";

const referMsg = (t, code) => `${t("Give your dry scrap to Kindsaver and get paid. Use my referral code")} ${code}: ${window.location.origin}/?ref=${code}`;

const share = async (text, file) => {
  try {
    if (file && navigator.canShare?.({ files: [file] })) return await navigator.share({ files: [file], text });
    if (navigator.share) return await navigator.share({ text });
  } catch (e) { if (e?.name === "AbortError") return; }
  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
};

export const ShareCode = ({ code }) => {
  const { t } = useApp();
  if (!code) return null;
  return (
    <button onClick={() => share(referMsg(t, code))} data-testid="lb-share-code-btn"
      className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-[#25D366] text-base font-bold text-white transition-transform active:scale-[.97]">
      <Share2 className="h-5 w-5" />{t("Share my referral code")} · {code}</button>
  );
};

const drawCard = async (w, logo) => {
  const c = document.createElement("canvas");
  c.width = 1080; c.height = 1080;
  const g = c.getContext("2d");
  g.fillStyle = "#0D3B2B"; g.fillRect(0, 0, 1080, 1080);
  g.fillStyle = "#2E9E6B"; g.beginPath(); g.arc(900, 160, 260, 0, Math.PI * 2); g.fill();
  g.fillStyle = "#F5B83D"; g.beginPath(); g.arc(540, 250, 100, 0, Math.PI * 2); g.fill();
  g.fillStyle = "#0D3B2B"; g.font = "bold 96px sans-serif"; g.textAlign = "center"; g.fillText("#1", 540, 285);
  g.fillStyle = "#9BE3BF"; g.font = "bold 44px sans-serif"; g.fillText(`TOP RECYCLER · ${w.month}`, 540, 430);
  g.fillStyle = "#fff"; g.font = "bold 84px sans-serif"; g.fillText("My circle's #1", 540, 540);
  g.font = "bold 120px sans-serif"; g.fillText(`${w.kg} kg`, 540, 700);
  g.fillStyle = "#CFEBDD"; g.font = "40px sans-serif"; g.fillText(`recycled · ${w.co2} kg CO₂ saved`, 540, 770);
  g.fillText("Join me on Kindsaver Pickup, Jaipur", 540, 900);
  try {
    const img = await new Promise((ok, no) => { const i = new Image(); i.onload = () => ok(i); i.onerror = no; i.src = logo; });
    const h = 90, wd = (img.width / img.height) * h; g.drawImage(img, 540 - wd / 2, 950, wd, h);
  } catch { /* logo optional */ }
  return new Promise((ok) => c.toBlob((b) => ok(new File([b], "kindsaver-circle-winner.png", { type: "image/png" })), "image/png"));
};

export const CircleWinner = ({ w, code }) => {
  const { t } = useApp();
  if (!w) return null;
  const go = async (download) => {
    const file = await drawCard(w, "/logo-tagline.webp");
    if (download) { const a = document.createElement("a"); a.href = URL.createObjectURL(file); a.download = file.name; a.click(); return toast.success(t("Card saved")); }
    share(`${t("I was the top recycler in my Kindsaver circle!")} ${referMsg(t, code)}`, file);
  };
  return (
    <div className="space-y-3 rounded-3xl bg-[#0D3B2B] p-5 text-white" data-testid="lb-circle-winner">
      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#F5B83D]"><Crown className="h-5 w-5" />{t("Last month's circle winner")} · {w.month}</p>
      <p className="font-display text-2xl font-extrabold" data-testid="lb-circle-winner-name">{w.is_me ? t("You!") : w.name}</p>
      <p className="text-sm text-[#CFEBDD]">{w.kg} kg · {w.co2} kg CO₂ {t("saved")} · +{w.points} {t("bonus points")}</p>
      {w.is_me && (
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => go(false)} className="flex h-12 items-center justify-center gap-2 rounded-2xl bg-[#F5B83D] font-bold text-[#0D3B2B]" data-testid="lb-winner-share-btn"><Share2 className="h-5 w-5" />{t("Share my win")}</button>
          <button onClick={() => go(true)} className="flex h-12 items-center justify-center gap-2 rounded-2xl bg-white/15 font-bold" data-testid="lb-winner-download-btn"><Download className="h-5 w-5" />{t("Save card")}</button>
        </div>
      )}
    </div>
  );
};
